import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, API, getToken } from "../lib/api";
import { useAuth } from "./AuthContext";

const AudioContext = createContext(null);
export const useAudioSystem = () => useContext(AudioContext);

export function AudioProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState("stopped"); // stopped | music | priority | muted
  const [masterVolume, setMasterVolume] = useState(70);
  const [muted, setMuted] = useState(false);
  const [priorityNow, setPriorityNow] = useState(null); // {name, category}
  const [musicTrack, setMusicTrack] = useState(null); // {name, artist}
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [settings, setSettings] = useState({ confirm_stop_all: true, default_resume_music: false, master_volume: 70 });
  const [online, setOnline] = useState(navigator.onLine);

  const audioElRef = useRef(null);
  const spotifyApiRef = useRef(null);
  const blobCacheRef = useRef({}); // audioId -> objectURL
  const currentAuditRef = useRef(null);

  useEffect(() => {
    if (!audioElRef.current) {
      const el = new Audio();
      el.preload = "auto";
      audioElRef.current = el;
    }
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/settings").then((res) => {
      setSettings(res.data);
      setMasterVolume(res.data.master_volume ?? 70);
    }).catch(() => {});
  }, [user]);

  // keep element volume in sync
  useEffect(() => {
    if (audioElRef.current) audioElRef.current.volume = muted ? 0 : masterVolume / 100;
  }, [masterVolume, muted]);

  const registerSpotify = useCallback((apiObj) => {
    spotifyApiRef.current = apiObj;
  }, []);

  const reportMusicState = useCallback((playing, track) => {
    setMusicPlaying(playing);
    if (track) setMusicTrack(track);
    setStatus((prev) => {
      if (prev === "priority" || prev === "muted") return prev;
      return playing ? "music" : "stopped";
    });
  }, []);

  // Prefetch critical (offline-resilient) audio into memory blobs
  const prefetchCritical = useCallback(async (files) => {
    const token = getToken();
    for (const f of files) {
      if (blobCacheRef.current[f.id]) continue;
      try {
        const res = await fetch(`${API}/audio/${f.id}/file?token=${token}`);
        if (!res.ok) continue;
        const blob = await res.blob();
        blobCacheRef.current[f.id] = URL.createObjectURL(blob);
      } catch (e) { /* offline / unavailable */ }
    }
  }, []);

  const stopAll = useCallback(() => {
    const el = audioElRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
      el.onended = null;
    }
    if (spotifyApiRef.current) spotifyApiRef.current.pause();
    if (currentAuditRef.current) {
      api.patch(`/audit/${currentAuditRef.current}`, { completed: false }).catch(() => {});
      currentAuditRef.current = null;
    }
    setPriorityNow(null);
    setMuted(false);
    setStatus("stopped");
    setMusicPlaying(false);
  }, []);

  const playPriority = useCallback(async (file) => {
    const el = audioElRef.current;
    if (!el) return;
    const wasPlaying = musicPlaying;

    // audit start
    try {
      const res = await api.post("/audit", {
        audio_id: file.id, audio_name: file.name, category: file.category,
        music_was_playing: wasPlaying,
      });
      currentAuditRef.current = res.data.id;
    } catch (e) { currentAuditRef.current = null; }

    // ensure music does not compete for speakers
    if (spotifyApiRef.current) spotifyApiRef.current.pause();

    const src = blobCacheRef.current[file.id] || `${API}/audio/${file.id}/file?token=${getToken()}`;
    el.src = src;
    el.volume = muted ? 0 : masterVolume / 100;
    el.currentTime = 0;

    const resumeAllowed = file.resume_music && settings.default_resume_music && wasPlaying;
    el.onended = () => {
      if (currentAuditRef.current) {
        api.patch(`/audit/${currentAuditRef.current}`, { completed: true }).catch(() => {});
        currentAuditRef.current = null;
      }
      setPriorityNow(null);
      if (resumeAllowed && spotifyApiRef.current) {
        spotifyApiRef.current.resume();
        setStatus("music");
      } else {
        setStatus("stopped");
      }
    };

    try {
      await el.play();
      setPriorityNow({ name: file.name, category: file.category });
      setStatus("priority");
    } catch (e) {
      setStatus("stopped");
    }
  }, [musicPlaying, muted, masterVolume, settings]);

  const changeVolume = useCallback((v) => {
    const clamped = Math.max(0, Math.min(100, v));
    setMasterVolume(clamped);
    if (clamped > 0 && muted) setMuted(false);
  }, [muted]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (next && spotifyApiRef.current) spotifyApiRef.current.pause();
      setStatus((prev) => {
        if (prev === "priority") return prev;
        return next ? "muted" : (musicPlaying ? "music" : "stopped");
      });
      return next;
    });
  }, [musicPlaying]);

  const value = {
    status, masterVolume, muted, priorityNow, musicTrack, musicPlaying, settings, online,
    setSettings, registerSpotify, reportMusicState, prefetchCritical,
    playPriority, stopAll, changeVolume, toggleMute,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
}
